import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { User, X, Settings, MessageCircle, Camera, Check, Pencil, MapPin, Trash2, ChevronRight, ArrowLeft, Wallet, Bell, BellOff, LogOut, KeyRound, Send, FolderTree, Package, ShoppingBag, Newspaper, ImageIcon, Sun, Moon, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { BottomNavigation } from "@/components/BottomNavigation";
import { PageTransition } from "@/components/PageTransition";
import { MenuDrawer } from "@/components/MenuDrawer";
import { toast } from "sonner";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

import SavedPaymentMethods from "@/components/SavedPaymentMethods";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useAdminRole } from "@/hooks/useAdminRole";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import CategoryManager from "@/components/CategoryManager";
import ProductCategoryManager from "@/components/ProductCategoryManager";
import WallpaperCategoryManager from "@/components/WallpaperCategoryManager";
import UserOrders from "@/components/UserOrders";
import AdminOrders from "@/components/AdminOrders";
import NewsManager from "@/components/NewsManager";
import WallpaperDeleteManager from "@/components/WallpaperDeleteManager";
import AdminUsersTable from "@/components/AdminUsersTable";
import IPhoneImageManager from "@/components/IPhoneImageManager";
import { useTheme } from "@/hooks/useTheme";
import { Users as UsersIcon, Smartphone as SmartphoneIcon } from "lucide-react";

interface Profile {
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  username: string | null;
  messaging_enabled: boolean;
}

const Profile = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedFirstName, setEditedFirstName] = useState("");
  const [editedLastName, setEditedLastName] = useState("");
  const [editedUsername, setEditedUsername] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [locationPermission, setLocationPermission] = useState<string | null>(null);
  
  const [isPaymentMethodsOpen, setIsPaymentMethodsOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationBody, setNotificationBody] = useState("");
  const [notificationType, setNotificationType] = useState<"alert" | "silent">("alert");
  const [sendingNotification, setSendingNotification] = useState(false);
  const [lastFailedEmails, setLastFailedEmails] = useState<string[]>([]);
  const [lastEmailPayload, setLastEmailPayload] = useState<{ title: string; body: string; url: string } | null>(null);
  const [retryingEmails, setRetryingEmails] = useState(false);
  const [isCategoryManagerOpen, setIsCategoryManagerOpen] = useState(false);
  const [isProductCategoryManagerOpen, setIsProductCategoryManagerOpen] = useState(false);
  const [isUserOrdersOpen, setIsUserOrdersOpen] = useState(false);
  const [isAdminOrdersOpen, setIsAdminOrdersOpen] = useState(false);
  const [isNewsManagerOpen, setIsNewsManagerOpen] = useState(false);
  const [isWallpaperCategoryManagerOpen, setIsWallpaperCategoryManagerOpen] = useState(false);
  const [isWallpaperDeleteManagerOpen, setIsWallpaperDeleteManagerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isAdminUsersOpen, setIsAdminUsersOpen] = useState(false);
  const [isIPhoneImageManagerOpen, setIsIPhoneImageManagerOpen] = useState(false);
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, isLoading: pushLoading, toggleSubscription } = usePushNotifications();
  const { isAdmin } = useAdminRole(userId);
  const { preference: themePreference, setTheme } = useTheme();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate("/auth");
        return;
      }

      setEmail(session.user.email || null);
      setUserId(session.user.id);

      // Fetch profile data
      const { data: profileData } = await supabase
        .from("profiles")
        .select("first_name, last_name, avatar_url, username, messaging_enabled")
        .eq("user_id", session.user.id)
        .maybeSingle();

      setProfile(profileData);
      if (profileData) {
        setEditedFirstName(profileData.first_name || "");
        setEditedLastName(profileData.last_name || "");
        setEditedUsername(profileData.username || "");
      }

      setLoading(false);

      // Check location permission status
      if (navigator.permissions) {
        navigator.permissions.query({ name: 'geolocation' }).then((result) => {
          setLocationPermission(result.state);
          result.onchange = () => setLocationPermission(result.state);
        });
      }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session) {
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleLocationPermission = () => {
    navigator.geolocation.getCurrentPosition(
      () => {
        setLocationPermission('granted');
        toast.success("Posizione attivata");
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          setLocationPermission('denied');
          toast.error("Permesso posizione negato. Modifica dalle impostazioni del browser.");
        }
      }
    );
  };

  const handleClearCache = async () => {
    try {
      // Clear localStorage
      localStorage.clear();
      
      // Clear sessionStorage
      sessionStorage.clear();
      
      // Clear caches if available
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      
      toast.success("Cache svuotata con successo");
    } catch (error) {
      console.error("Error clearing cache:", error);
      toast.error("Errore durante la pulizia della cache");
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !userId) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Seleziona un'immagine valida");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'immagine deve essere inferiore a 5MB");
      return;
    }

    setIsSaving(true);

    try {
      const fileExt = file.name.split(".").pop();
      const filePath = `${userId}/${Date.now()}.${fileExt}`;

      // Upload file to storage
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from("avatars")
        .getPublicUrl(filePath);

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: publicUrl })
        .eq("user_id", userId);

      if (updateError) throw updateError;

      setProfile(prev => prev ? { ...prev, avatar_url: publicUrl } : null);
      toast.success("Foto profilo aggiornata");
    } catch (error) {
      console.error("Error uploading avatar:", error);
      toast.error("Errore durante il caricamento dell'immagine");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditName = () => {
    setIsEditingName(true);
  };

  const handleSaveName = async () => {
    if (!userId) return;

    setIsSaving(true);

    try {
      const { error } = await supabase
        .from("profiles")
        .update({ 
          first_name: editedFirstName.trim() || null, 
          last_name: editedLastName.trim() || null,
          username: editedUsername.trim() || null,
        })
        .eq("user_id", userId);

      if (error) {
        if (error.code === '23505') {
          toast.error("Username già in uso, scegline un altro");
          return;
        }
        throw error;
      }

      setProfile(prev => prev ? { 
        ...prev, 
        first_name: editedFirstName.trim() || null, 
        last_name: editedLastName.trim() || null,
        username: editedUsername.trim() || null,
      } : null);
      setIsEditingName(false);
      toast.success("Profilo aggiornato");
    } catch (error) {
      console.error("Error updating name:", error);
      toast.error("Errore durante il salvataggio");
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error("Compila tutti i campi");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }

    setIsChangingPassword(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      toast.success("Password modificata con successo");
      setIsChangePasswordOpen(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      console.error("Error changing password:", error);
      toast.error(error.message || "Errore durante il cambio password");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const sendAdminPushNotification = async () => {
    if (!notificationTitle.trim() || !notificationBody.trim()) {
      toast.error("Inserisci titolo e messaggio della notifica");
      return;
    }

    setSendingNotification(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-push-notification', {
        body: {
          type: 'all',
          payload: {
            title: notificationTitle,
            body: notificationBody,
            icon: '/favicon.ico',
            url: '/',
            tag: notificationType === 'silent' ? 'tecly-silent' : 'tecly-broadcast',
            silent: notificationType === 'silent',
            requireInteraction: notificationType === 'alert',
          }
        }
      });

      if (error) throw error;

      const failedRecipients: string[] = data?.emails?.failedRecipients ?? [];
      const payloadEcho = data?.lastPayload ?? {
        title: notificationTitle,
        body: notificationBody,
        url: '/',
      };
      setLastFailedEmails(failedRecipients);
      setLastEmailPayload(payloadEcho);

      const emailsFailedCount = data?.emails?.failed ?? failedRecipients.length;
      if (emailsFailedCount > 0) {
        toast.success(
          `Push inviata a ${data?.sent || 0} utenti · ${emailsFailedCount} email fallite (puoi riprovarle)`
        );
      } else {
        toast.success(`Notifica inviata a ${data?.sent || 0} utenti (anche ad app chiusa)`);
      }
      setNotificationTitle("");
      setNotificationBody("");
    } catch (error) {
      console.error("Error sending notification:", error);
      toast.error("Errore durante l'invio della notifica");
    } finally {
      setSendingNotification(false);
    }
  };

  const retryFailedEmails = async () => {
    if (!lastEmailPayload) {
      toast.error("Nessuna email da riprovare");
      return;
    }
    setRetryingEmails(true);
    try {
      const { data, error } = await supabase.functions.invoke('retry-failed-emails', {
        body: {
          title: lastEmailPayload.title,
          body: lastEmailPayload.body,
          url: lastEmailPayload.url,
          // Prefer explicit list from the previous send; fall back to log lookup server-side.
          recipients: lastFailedEmails.length > 0 ? lastFailedEmails : undefined,
          sinceMinutes: 60,
        },
      });
      if (error) throw error;
      const sent = data?.sent ?? 0;
      const failed = data?.failed ?? 0;
      if (sent > 0 && failed === 0) {
        toast.success(`Tutte le ${sent} email sono state inviate correttamente`);
        setLastFailedEmails([]);
      } else if (sent > 0) {
        toast.success(`${sent} email inviate, ${failed} ancora fallite`);
        setLastFailedEmails(data?.stillFailed ?? []);
      } else {
        toast.error(`Nessuna email inviata (${failed} fallite)`);
        setLastFailedEmails(data?.stillFailed ?? lastFailedEmails);
      }
    } catch (e) {
      console.error('Error retrying failed emails', e);
      toast.error("Errore durante il retry delle email");
    } finally {
      setRetryingEmails(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const fullName = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || "Utente";

  return (
    <PageTransition>
      <div className="min-h-screen bg-background pb-32">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-background/90 backdrop-blur-lg border-b border-border/50">
          <div className="container mx-auto px-4 py-4 flex items-center gap-3">
            <button
              onClick={() => navigate("/")}
              aria-label="Chiudi"
              className="transition-all duration-300 active:scale-90"
            >
              <X className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold text-foreground">Profilo</h1>
          </div>
        </header>

        {/* Profile Content */}
        <main className="container mx-auto px-4 py-8">
          <div className="flex flex-col items-center gap-4 mb-8">
            {/* Avatar with edit capability */}
            <div className="relative group">
              <button
                onClick={handleAvatarClick}
                disabled={isSaving}
                className="w-24 h-24 rounded-full bg-muted flex items-center justify-center overflow-hidden transition-all duration-300 hover:scale-105 active:scale-95 ring-2 ring-transparent hover:ring-primary/50"
              >
                {profile?.avatar_url ? (
                  <img 
                    src={profile.avatar_url} 
                    alt="Avatar" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-12 h-12 text-muted-foreground" />
                )}
              </button>
              <div className="absolute bottom-0 right-0 p-1.5 bg-primary rounded-full text-primary-foreground shadow-lg transition-all duration-300 group-hover:scale-110">
                <Camera className="w-4 h-4" />
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>

            {/* Name with edit capability */}
            {isEditingName ? (
              <div className="flex flex-col items-center gap-3 w-full max-w-xs">
                <input
                  type="text"
                  value={editedUsername}
                  onChange={(e) => setEditedUsername(e.target.value.toLowerCase().replace(/[^a-z0-9._]/g, ''))}
                  placeholder="Username (obbligatorio per la Bacheca)"
                  className="w-full px-4 py-2 rounded-lg bg-muted border border-border text-foreground text-center focus:outline-none focus:ring-2 focus:ring-primary"
                  maxLength={30}
                />
                <input
                  type="text"
                  value={editedFirstName}
                  onChange={(e) => setEditedFirstName(e.target.value)}
                  placeholder="Nome (opzionale)"
                  className="w-full px-4 py-2 rounded-lg bg-muted border border-border text-foreground text-center focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <input
                  type="text"
                  value={editedLastName}
                  onChange={(e) => setEditedLastName(e.target.value)}
                  placeholder="Cognome (opzionale)"
                  className="w-full px-4 py-2 rounded-lg bg-muted border border-border text-foreground text-center focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <button
                  onClick={handleSaveName}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-all duration-300 hover:scale-105 active:scale-95 disabled:opacity-50"
                >
                  <Check className="w-4 h-4" />
                  <span>Salva</span>
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1">
                <button
                  onClick={handleEditName}
                  className="group flex items-center gap-2 transition-all duration-300 hover:scale-105 active:scale-95"
                >
                  <h2 className="text-2xl font-bold text-foreground">{fullName}</h2>
                  <Pencil className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </button>
                {profile?.username && (
                  <p className="text-sm text-primary font-medium">@{profile.username}</p>
                )}
                {!profile?.username && (
                  <button onClick={handleEditName} className="text-xs text-primary hover:underline">
                    Imposta username per la Bacheca
                  </button>
                )}
              </div>
            )}

            {/* Email */}
            <p className="text-sm text-muted-foreground">{email}</p>

          </div>

          {/* Menu Options */}
          <div className="space-y-3">
            {/* 1. Impostazioni */}
            <button
              onClick={() => setIsSettingsOpen(true)}
              className="w-full flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-blue-500/20 to-blue-600/10 hover:from-blue-500/30 hover:to-blue-600/20 border border-blue-500/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            >
              <Settings className="w-5 h-5 text-blue-400" />
              <span className="font-medium bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">Impostazioni</span>
              <ChevronRight className="w-5 h-5 text-blue-400/60 ml-auto" />
            </button>

            {/* 2. Notifiche Push */}
            {pushSupported && (
              <button
                onClick={toggleSubscription}
                disabled={pushLoading}
                className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 ${
                  pushSubscribed
                    ? "bg-gradient-to-r from-green-500/20 to-emerald-600/10 border border-green-500/20"
                    : "bg-gradient-to-r from-orange-500/20 to-amber-600/10 border border-orange-500/20"
                }`}
              >
                {pushSubscribed ? (
                  <Bell className="w-5 h-5 text-green-400" />
                ) : (
                  <BellOff className="w-5 h-5 text-orange-400" />
                )}
                <div className="flex flex-col items-start flex-1">
                  <span className={`font-medium ${pushSubscribed ? "text-green-400" : "text-orange-400"}`}>
                    Notifiche Push
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {pushLoading ? "Caricamento..." : pushSubscribed ? "Attive — ricevi aggiornamenti su prodotti, news e messaggi" : "Disattivate — attiva per non perdere nessuna novità"}
                  </span>
                </div>
                {!pushLoading && (
                  <Badge variant={pushSubscribed ? "default" : "outline"} className={pushSubscribed ? "bg-green-500/80 text-white" : "border-orange-400/50 text-orange-400"}>
                    {pushSubscribed ? "ON" : "OFF"}
                  </Badge>
                )}
              </button>
            )}

            {/* 3. Contattaci */}
            <a
              href="mailto:assistenza.app26@gmail.com"
              className="w-full flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            >
              <MessageCircle className="w-5 h-5 text-foreground" />
              <span className="text-foreground font-medium">Contattaci</span>
            </a>


            {/* 5. Stato Ordine */}
            <button
              onClick={() => setIsUserOrdersOpen(true)}
              className="w-full flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            >
              <ShoppingBag className="w-5 h-5 text-foreground" />
              <span className="text-foreground font-medium">Stato Ordine</span>
              <ChevronRight className="w-5 h-5 text-muted-foreground ml-auto" />
            </button>

            {/* 6. Metodi di Pagamento */}
            <button
              onClick={() => setIsPaymentMethodsOpen(true)}
              className="w-full flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            >
              <Wallet className="w-5 h-5 text-foreground" />
              <span className="text-foreground font-medium">Metodi di Pagamento</span>
              <ChevronRight className="w-5 h-5 text-muted-foreground ml-auto" />
            </button>


            {/* 8. Modifica Password */}
            <button
              onClick={() => setIsChangePasswordOpen(true)}
              className="w-full flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            >
              <KeyRound className="w-5 h-5 text-foreground" />
              <span className="text-foreground font-medium">Modifica Password</span>
              <ChevronRight className="w-5 h-5 text-muted-foreground ml-auto" />
            </button>

            {/* 9. Esci */}
            <button
              onClick={async () => {
                await supabase.auth.signOut();
                toast.success("Logout effettuato");
                navigate("/auth");
              }}
              className="w-full flex items-center gap-4 p-4 rounded-xl bg-destructive/10 hover:bg-destructive/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            >
              <LogOut className="w-5 h-5 text-destructive" />
              <span className="text-destructive font-medium">Esci</span>
            </button>

            {/* Admin Panel */}
            {isAdmin && (
              <>
                <button
                  onClick={() => setIsNotificationPanelOpen(true)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-primary/10 hover:bg-primary/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <Send className="w-5 h-5 text-primary" />
                  <span className="text-primary font-medium">Invia Notifiche Push</span>
                  <ChevronRight className="w-5 h-5 text-primary/60 ml-auto" />
                </button>

                <button
                  onClick={() => setIsAdminOrdersOpen(true)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-primary/10 hover:bg-primary/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <ShoppingBag className="w-5 h-5 text-primary" />
                  <span className="text-primary font-medium">Ordini Clienti</span>
                  <ChevronRight className="w-5 h-5 text-primary/60 ml-auto" />
                </button>

                <button
                  onClick={() => setIsNewsManagerOpen(true)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-primary/10 hover:bg-primary/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <Newspaper className="w-5 h-5 text-primary" />
                  <span className="text-primary font-medium">Gestione News</span>
                  <ChevronRight className="w-5 h-5 text-primary/60 ml-auto" />
                </button>

                <button
                  onClick={() => setIsCategoryManagerOpen(true)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-primary/10 hover:bg-primary/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <FolderTree className="w-5 h-5 text-primary" />
                  <span className="text-primary font-medium">Gestione Categorie News</span>
                  <ChevronRight className="w-5 h-5 text-primary/60 ml-auto" />
                </button>

                <button
                  onClick={() => setIsProductCategoryManagerOpen(true)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-primary/10 hover:bg-primary/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <Package className="w-5 h-5 text-primary" />
                  <span className="text-primary font-medium">Gestione Categorie Prodotti</span>
                  <ChevronRight className="w-5 h-5 text-primary/60 ml-auto" />
                </button>

                <button
                  onClick={() => setIsWallpaperCategoryManagerOpen(true)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-primary/10 hover:bg-primary/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <ImageIcon className="w-5 h-5 text-primary" />
                  <span className="text-primary font-medium">Gestione Categorie Sfondi</span>
                  <ChevronRight className="w-5 h-5 text-primary/60 ml-auto" />
                </button>

                <button
                  onClick={() => setIsWallpaperDeleteManagerOpen(true)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-destructive/10 hover:bg-destructive/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <Trash2 className="w-5 h-5 text-destructive" />
                  <span className="text-destructive font-medium">Elimina Sfondi</span>
                  <ChevronRight className="w-5 h-5 text-destructive/60 ml-auto" />
                </button>

                <button
                  onClick={() => setIsIPhoneImageManagerOpen(true)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-primary/10 hover:bg-primary/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <SmartphoneIcon className="w-5 h-5 text-primary" />
                  <span className="text-primary font-medium">Immagini iPhone</span>
                  <ChevronRight className="w-5 h-5 text-primary/60 ml-auto" />
                </button>

                <button
                  onClick={() => setIsAdminUsersOpen(true)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-primary/10 hover:bg-primary/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <UsersIcon className="w-5 h-5 text-primary" />
                  <span className="text-primary font-medium">Tabella Utenti</span>
                  <ChevronRight className="w-5 h-5 text-primary/60 ml-auto" />
                </button>
              </>
            )}
          </div>
        </main>

        {/* Bottom Navigation */}
        {userId && (
          <>
            <BottomNavigation onMenuClick={() => setIsMenuOpen(true)} activeTab="profile" />
            <MenuDrawer open={isMenuOpen} onOpenChange={setIsMenuOpen} />
          </>
        )}

        {/* Settings — centered Liquid Glass modal (same animation as password/orders) */}
        {isSettingsOpen && (
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center p-5"
            style={{ background: "rgba(0,0,0,0.35)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
            onClick={() => setIsSettingsOpen(false)}
            role="dialog"
            aria-modal="true"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-3xl animate-scale-in flex flex-col overflow-hidden"
              style={{
                maxHeight: "min(82vh, 720px)",
                background: "rgba(245,245,250,0.92)",
                backdropFilter: "blur(40px) saturate(180%)",
                WebkitBackdropFilter: "blur(40px) saturate(180%)",
                border: "1px solid rgba(255,255,255,0.5)",
                boxShadow: "0 24px 60px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.5)",
              }}
            >
              <div className="flex items-center justify-between px-5 pt-5 pb-3">
                <h3 className="text-lg font-semibold text-foreground">Impostazioni</h3>
                <button
                  onClick={() => setIsSettingsOpen(false)}
                  className="p-1.5 rounded-full hover:bg-black/5 active:scale-90 transition"
                  aria-label="Chiudi"
                >
                  <X className="w-5 h-5 text-foreground/70" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 pb-5">
                <div className="flex flex-col gap-3">
                  {/* Location Permission */}
                  <button
                    onClick={handleLocationPermission}
                    className="w-full flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-all duration-300 active:scale-[0.98]"
                  >
                    <MapPin className="w-5 h-5 text-foreground" />
                    <div className="flex flex-col items-start flex-1">
                      <span className="text-foreground font-medium">Posizione</span>
                      <span className="text-xs text-muted-foreground">
                        {locationPermission === 'granted' ? 'Attivata' : locationPermission === 'denied' ? 'Negata' : 'Non configurata'}
                      </span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </button>

                  {/* Push Notifications */}
                  {pushSupported && (
                    <button
                      onClick={toggleSubscription}
                      disabled={pushLoading}
                      className="w-full flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-all duration-300 active:scale-[0.98] disabled:opacity-50"
                    >
                      {pushSubscribed ? (
                        <Bell className="w-5 h-5 text-primary" />
                      ) : (
                        <BellOff className="w-5 h-5 text-foreground" />
                      )}
                      <div className="flex flex-col items-start flex-1">
                        <span className="text-foreground font-medium">Notifiche Push</span>
                        <span className="text-xs text-muted-foreground">
                          {pushLoading ? 'Caricamento...' : pushSubscribed ? 'Attive' : 'Disattivate'}
                        </span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </button>
                  )}

                  {/* Clear Cache */}
                  <button
                    onClick={handleClearCache}
                    className="w-full flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-all duration-300 active:scale-[0.98]"
                  >
                    <Trash2 className="w-5 h-5 text-foreground" />
                    <span className="text-foreground font-medium">Svuota cache</span>
                    <ChevronRight className="w-5 h-5 text-muted-foreground ml-auto" />
                  </button>

                  {/* Theme Selection */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-foreground px-1">Tema App</p>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={() => setTheme("classic")}
                        className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-all duration-300 active:scale-[0.98] ${
                          themePreference === "classic"
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border/50 bg-muted/50 text-foreground hover:bg-muted"
                        }`}
                      >
                        <Sun className="w-5 h-5" />
                        <span className="text-xs font-medium">Classico</span>
                      </button>
                      <button
                        onClick={() => setTheme("dark")}
                        className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-all duration-300 active:scale-[0.98] ${
                          themePreference === "dark"
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border/50 bg-muted/50 text-foreground hover:bg-muted"
                        }`}
                      >
                        <Moon className="w-5 h-5" />
                        <span className="text-xs font-medium">Dark Mode</span>
                      </button>
                      <button
                        onClick={() => setTheme("auto")}
                        className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-all duration-300 active:scale-[0.98] ${
                          themePreference === "auto"
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border/50 bg-muted/50 text-foreground hover:bg-muted"
                        }`}
                      >
                        <Clock className="w-5 h-5" />
                        <span className="text-xs font-medium">Automatico</span>
                      </button>
                    </div>
                    {themePreference === "auto" && (
                      <p className="text-xs text-muted-foreground px-1">
                        Classico 06:00–18:00, Dark Mode 18:00–06:00
                      </p>
                    )}
                  </div>

                  {/* Messaging Toggle */}
                  <button
                    onClick={async () => {
                      if (!userId || !profile) return;
                      const newVal = !profile.messaging_enabled;
                      const { error } = await supabase
                        .from("profiles")
                        .update({ messaging_enabled: newVal } as any)
                        .eq("user_id", userId);
                      if (!error) {
                        setProfile(prev => prev ? { ...prev, messaging_enabled: newVal } : null);
                        toast.success(newVal ? "Messaggi attivati" : "Messaggi disattivati");
                      }
                    }}
                    className="w-full flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-all duration-300 active:scale-[0.98]"
                  >
                    <MessageCircle className="w-5 h-5 text-foreground" />
                    <div className="flex flex-col items-start flex-1">
                      <span className="text-foreground font-medium">Messaggi dal Profilo</span>
                      <span className="text-xs text-muted-foreground">
                        {profile?.messaging_enabled !== false ? "Gli altri utenti possono inviarti messaggi" : "Nessuno può inviarti messaggi dal profilo"}
                      </span>
                    </div>
                    <Badge variant={profile?.messaging_enabled !== false ? "default" : "outline"} className={profile?.messaging_enabled !== false ? "bg-primary/80 text-primary-foreground" : "border-muted-foreground/50 text-muted-foreground"}>
                      {profile?.messaging_enabled !== false ? "ON" : "OFF"}
                    </Badge>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}



        {/* Saved Payment Methods Drawer */}
        <SavedPaymentMethods
          isOpen={isPaymentMethodsOpen}
          onClose={() => setIsPaymentMethodsOpen(false)}
        />

        {/* Change Password — minimal centered modal */}
        {isChangePasswordOpen && (
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center p-5"
            style={{ background: "rgba(0,0,0,0.35)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
            onClick={() => setIsChangePasswordOpen(false)}
            role="dialog"
            aria-modal="true"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm rounded-3xl p-6 animate-scale-in"
              style={{
                background: "rgba(245,245,250,0.92)",
                backdropFilter: "blur(40px) saturate(180%)",
                WebkitBackdropFilter: "blur(40px) saturate(180%)",
                border: "1px solid rgba(255,255,255,0.5)",
                boxShadow: "0 24px 60px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.5)",
              }}
            >
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-semibold text-foreground">Modifica Password</h3>
                <button
                  onClick={() => setIsChangePasswordOpen(false)}
                  className="p-1.5 rounded-full hover:bg-black/5 active:scale-90 transition"
                  aria-label="Chiudi"
                >
                  <X className="w-5 h-5 text-foreground/70" />
                </button>
              </div>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-medium uppercase tracking-wider text-foreground/50">Nuova password</label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-black/[0.04] border-black/10 rounded-xl h-11"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-medium uppercase tracking-wider text-foreground/50">Conferma password</label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-black/[0.04] border-black/10 rounded-xl h-11"
                  />
                </div>
              </div>
              <div className="flex gap-2.5 mt-5">
                <button
                  onClick={() => setIsChangePasswordOpen(false)}
                  className="flex-1 py-3 rounded-2xl bg-black/[0.05] border border-black/10 font-medium text-foreground/70 active:scale-[0.97] transition"
                >
                  Annulla
                </button>
                <button
                  onClick={handleChangePassword}
                  disabled={isChangingPassword}
                  className="flex-1 py-3 rounded-2xl bg-primary text-primary-foreground font-semibold active:scale-[0.97] transition disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isChangingPassword ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    "Salva"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Admin Notification Panel Drawer */}
        {isAdmin && (
          <Drawer open={isNotificationPanelOpen} onOpenChange={setIsNotificationPanelOpen} shouldScaleBackground={false}>
            <DrawerContent className="h-full bg-background/[0.78] backdrop-blur-lg border-none">
              <DrawerHeader className="relative">
                <button 
                  onClick={() => setIsNotificationPanelOpen(false)}
                  aria-label="Indietro"
                  className="absolute left-4 top-1/2 -translate-y-1/2"
                >
                  <ArrowLeft className="w-6 h-6 text-foreground" />
                </button>
                <DrawerTitle className="text-2xl font-bold text-center">Invia Notifica Push</DrawerTitle>
              </DrawerHeader>
              <div className="flex flex-col gap-4 p-6 max-w-md mx-auto w-full overflow-y-auto">
                <div className="p-3 bg-primary/10 rounded-xl">
                  <p className="text-xs text-primary leading-relaxed">
                    <Bell className="w-3.5 h-3.5 inline mr-1.5" />
                    Inviata a <strong>tutti gli iscritti</strong> con priorità alta (arriva anche ad <strong>app chiusa</strong>).
                  </p>
                </div>

                {(() => {
                  if (typeof window === 'undefined') return null;
                  const ua = window.navigator.userAgent;
                  const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
                  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
                  const noPush = !('serviceWorker' in navigator) || !('PushManager' in window);
                  const iosNotInstalled = isIOS && !isStandalone;
                  if (!noPush && !iosNotInstalled) return null;
                  return (
                    <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30">
                      <p className="text-xs text-amber-600 dark:text-amber-400 leading-relaxed">
                        ⚠️ <strong>Background delivery limitato</strong>:{" "}
                        {noPush
                          ? "questo browser non supporta le push notifications."
                          : "su iOS le notifiche ad app chiusa funzionano solo se l'app è installata sulla Home (Aggiungi a schermata Home)."}
                      </p>
                    </div>
                  );
                })()}


                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Titolo</label>
                  <Input
                    type="text"
                    value={notificationTitle}
                    onChange={(e) => setNotificationTitle(e.target.value)}
                    placeholder="es. Nuova news disponibile!"
                    maxLength={60}
                    className="bg-muted/50"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Messaggio</label>
                  <Input
                    type="text"
                    value={notificationBody}
                    onChange={(e) => setNotificationBody(e.target.value)}
                    placeholder="es. iPhone 17 Pro recensione disponibile"
                    maxLength={140}
                    className="bg-muted/50"
                  />
                </div>

                {/* Type selector */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Tipo</label>
                  <div className="flex gap-2 p-1 bg-muted/40 rounded-xl">
                    {(["alert", "silent"] as const).map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setNotificationType(t)}
                        className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all ${
                          notificationType === t
                            ? "bg-primary text-primary-foreground shadow"
                            : "text-foreground/60 hover:text-foreground"
                        }`}
                      >
                        {t === "alert" ? "🔔 Alert (con suono)" : "🔕 Silent (solo badge)"}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Live preview */}
                <div className="space-y-2">
                  <label className="text-xs font-medium text-foreground/60 uppercase tracking-wide">Anteprima</label>
                  <div
                    className="rounded-2xl p-3 flex gap-3 items-start"
                    style={{
                      background: "rgba(245,245,250,0.92)",
                      backdropFilter: "blur(40px) saturate(180%)",
                      WebkitBackdropFilter: "blur(40px) saturate(180%)",
                      border: "1px solid rgba(255,255,255,0.6)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                    }}
                  >
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-sky-400 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                      T
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] font-semibold text-black/60 uppercase tracking-wide">TECLY</span>
                        <span className="text-[10px] text-black/40">ora</span>
                      </div>
                      <div className="text-[13px] font-semibold text-black truncate mt-0.5">
                        {notificationTitle || "Titolo della notifica"}
                      </div>
                      <div className="text-[12px] text-black/70 line-clamp-2 mt-0.5">
                        {notificationBody || "Anteprima del messaggio che vedranno gli utenti"}
                      </div>
                      {notificationType === "silent" && (
                        <div className="text-[10px] text-black/40 mt-1">Senza suono · solo badge</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Payload preview */}
                <div className="space-y-2">
                  <label className="text-xs font-medium text-foreground/60 uppercase tracking-wide">Payload inviato</label>
                  <div className="rounded-xl bg-muted/60 border border-border/50 p-3 space-y-1.5 text-[11px] font-mono leading-relaxed">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">type</span>
                      <span className="text-foreground font-semibold">all (broadcast)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">tag</span>
                      <span className="text-foreground font-semibold">
                        {notificationType === "silent" ? "tecly-silent" : "tecly-broadcast"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">silent</span>
                      <span className="text-foreground font-semibold">{notificationType === "silent" ? "true" : "false"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">requireInteraction</span>
                      <span className="text-foreground font-semibold">{notificationType === "alert" ? "true" : "false"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Urgency</span>
                      <span className="text-emerald-500 font-semibold">high</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">FCM priority</span>
                      <span className="text-emerald-500 font-semibold">high</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">APNs content-available</span>
                      <span className="text-emerald-500 font-semibold">1</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">APNs apns-priority</span>
                      <span className="text-emerald-500 font-semibold">10</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">APNs apns-push-type</span>
                      <span className="text-emerald-500 font-semibold">alert</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">FCM content_available</span>
                      <span className="text-emerald-500 font-semibold">true</span>
                    </div>
                    <div className="flex justify-between border-t border-border/30 pt-1.5 mt-1">
                      <span className="text-muted-foreground">timestamp</span>
                      <span className="text-foreground">{new Date().toISOString()}</span>
                    </div>
                  </div>
                  <p className="text-[10px] text-muted-foreground leading-relaxed">
                    I flag <span className="text-emerald-500 font-semibold">Urgency: high</span> e <span className="text-emerald-500 font-semibold">content-available: 1</span> garantiscono la consegna anche quando l&apos;app è completamente chiusa (killed state).
                  </p>
                </div>

                {/* Email preview */}
                <div className="space-y-2">
                  <label className="text-xs font-medium text-foreground/60 uppercase tracking-wide">Anteprima Email</label>
                  <div
                    className="rounded-2xl overflow-hidden border border-border/50"
                    style={{
                      background: "#ffffff",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                    }}
                  >
                    {/* Fake email header */}
                    <div className="px-4 py-3 border-b border-black/5 flex items-center gap-3 bg-gray-50/60">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-sky-400 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                        T
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-semibold text-black/80 leading-tight">TECLY</div>
                        <div className="text-[10px] text-black/40 leading-tight">a te</div>
                      </div>
                      <span className="text-[10px] text-black/30">ora</span>
                    </div>
                    {/* Fake email body */}
                    <div className="px-4 py-4 space-y-2">
                      <div className="text-[13px] font-bold text-black leading-snug">
                        {notificationTitle || "Titolo della notifica"}
                      </div>
                      <div className="text-[12px] text-black/70 leading-relaxed">
                        {notificationBody || "Anteprima del messaggio che vedranno gli utenti nell'email."}
                      </div>
                      <div className="pt-2">
                        <span className="inline-block px-3 py-1.5 rounded-lg bg-blue-500 text-white text-[11px] font-semibold">
                          Apri in app
                        </span>
                      </div>
                    </div>
                    {/* Fake email footer */}
                    <div className="px-4 py-2 border-t border-black/5 bg-gray-50/40">
                      <p className="text-[9px] text-black/30 text-center leading-relaxed">
                        Tecly · info@tecly.it<br />Se non vuoi più ricevere queste email, disattiva le notifiche dalle impostazioni.
                      </p>
                    </div>
                  </div>
                  <p className="text-[10px] text-muted-foreground leading-relaxed">
                    L&apos;email verrà inviata <strong>in parallelo</strong> alla push a tutti gli utenti con un indirizzo email valido.
                  </p>
                </div>

                <button
                  onClick={sendAdminPushNotification}
                  disabled={sendingNotification || !notificationTitle.trim() || !notificationBody.trim()}
                  className="w-full py-3 px-4 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {sendingNotification ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-foreground" />
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Invia a Tutti (anche ad app chiusa)
                    </>
                  )}
                </button>

                {lastEmailPayload && (
                  <div className="rounded-2xl border border-amber-300/40 bg-amber-50/60 dark:bg-amber-950/20 p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-amber-900 dark:text-amber-200 uppercase tracking-wide">
                          Retry Email
                        </p>
                        <p className="text-xs text-amber-900/80 dark:text-amber-100/80 leading-relaxed">
                          {lastFailedEmails.length > 0
                            ? `${lastFailedEmails.length} email non sono state consegnate nell'ultimo invio.`
                            : "Nessuna email fallita registrata: il retry cercherà nei log dell'ultima ora."}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={retryFailedEmails}
                      disabled={retryingEmails}
                      className="w-full py-2.5 px-4 bg-amber-500 hover:bg-amber-500/90 text-white rounded-xl text-sm font-medium transition-all duration-300 active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {retryingEmails ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      ) : (
                        <>Riprova solo le email fallite{lastFailedEmails.length > 0 ? ` (${lastFailedEmails.length})` : ""}</>
                      )}
                    </button>
                    <p className="text-[10px] text-amber-900/60 dark:text-amber-100/60 leading-relaxed">
                      Reinvia <strong>solo l&apos;email</strong> con lo stesso titolo e corpo dell&apos;ultima push, senza generare nuove notifiche.
                    </p>
                  </div>
                )}


                <a
                  href="/push-demo"
                  className="text-center text-xs text-primary/80 hover:text-primary underline underline-offset-2"
                >
                  Avanzato: apri Push Demo (test killed-state, payload FCM/APNs, log) →
                </a>
              </div>
            </DrawerContent>
          </Drawer>
        )}

        {/* Category Manager Drawer */}
        <CategoryManager 
          isOpen={isCategoryManagerOpen} 
          onClose={() => setIsCategoryManagerOpen(false)} 
        />

        {/* Product Category Manager Drawer */}
        <ProductCategoryManager 
          isOpen={isProductCategoryManagerOpen} 
          onClose={() => setIsProductCategoryManagerOpen(false)} 
        />

        {/* User Orders — centered Liquid Glass modal (like Modifica Password) */}
        {isUserOrdersOpen && (
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center p-5"
            style={{ background: "rgba(0,0,0,0.35)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
            onClick={() => setIsUserOrdersOpen(false)}
            role="dialog"
            aria-modal="true"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-3xl animate-scale-in flex flex-col overflow-hidden"
              style={{
                maxHeight: "min(80vh, 640px)",
                background: "rgba(245,245,250,0.92)",
                backdropFilter: "blur(40px) saturate(180%)",
                WebkitBackdropFilter: "blur(40px) saturate(180%)",
                border: "1px solid rgba(255,255,255,0.5)",
                boxShadow: "0 24px 60px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.5)",
              }}
            >
              <div className="flex items-center justify-between px-5 pt-5 pb-3">
                <h3 className="text-lg font-semibold text-foreground">Stato Ordine</h3>
                <button
                  onClick={() => setIsUserOrdersOpen(false)}
                  className="p-1.5 rounded-full hover:bg-black/5 active:scale-90 transition"
                  aria-label="Chiudi"
                >
                  <X className="w-5 h-5 text-foreground/70" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-2 pb-4">
                <UserOrders />
              </div>
            </div>
          </div>
        )}

        {/* Admin Orders Drawer */}
        {isAdmin && (
          <Drawer open={isAdminOrdersOpen} onOpenChange={setIsAdminOrdersOpen}>
            <DrawerContent className="h-[90vh] bg-background/95 backdrop-blur-xl border-t border-border/50">
              <DrawerHeader className="relative border-b border-border/30 pb-4">
                <button
                  onClick={() => setIsAdminOrdersOpen(false)}
                  aria-label="Indietro"
                  className="absolute left-4 top-1/2 -translate-y-1/2"
                >
                  <ArrowLeft className="w-5 h-5 text-foreground" />
                </button>
                <DrawerTitle className="text-xl font-bold text-center">Ordini Clienti</DrawerTitle>
              </DrawerHeader>
              <AdminOrders />
            </DrawerContent>
          </Drawer>
        )}

        {/* News Manager Drawer */}
        {isAdmin && (
          <Drawer open={isNewsManagerOpen} onOpenChange={setIsNewsManagerOpen}>
            <DrawerContent className="h-[90vh] bg-background/95 backdrop-blur-xl border-t border-border/50">
              <DrawerHeader className="relative border-b border-border/30 pb-4">
                <button
                  onClick={() => setIsNewsManagerOpen(false)}
                  aria-label="Indietro"
                  className="absolute left-4 top-1/2 -translate-y-1/2"
                >
                  <ArrowLeft className="w-5 h-5 text-foreground" />
                </button>
                <DrawerTitle className="text-xl font-bold text-center">Gestione News</DrawerTitle>
              </DrawerHeader>
              <NewsManager />
            </DrawerContent>
          </Drawer>
        )}

        {/* Wallpaper Category Manager */}
        {isAdmin && (
          <WallpaperCategoryManager
            isOpen={isWallpaperCategoryManagerOpen}
            onClose={() => setIsWallpaperCategoryManagerOpen(false)}
          />
        )}

        {/* iPhone Image Manager */}
        {isAdmin && (
          <IPhoneImageManager
            isOpen={isIPhoneImageManagerOpen}
            onClose={() => setIsIPhoneImageManagerOpen(false)}
          />
        )}

        {/* Wallpaper Delete Manager */}
        {isAdmin && (
          <WallpaperDeleteManager
            isOpen={isWallpaperDeleteManagerOpen}
            onClose={() => setIsWallpaperDeleteManagerOpen(false)}
          />
        )}

        {/* Admin Users Table Drawer */}
        {isAdmin && (
          <Drawer open={isAdminUsersOpen} onOpenChange={setIsAdminUsersOpen}>
            <DrawerContent className="h-[90vh] bg-background/95 backdrop-blur-xl border-t border-border/50">
              <DrawerHeader className="border-b border-border/50">
                <DrawerTitle className="flex items-center gap-2">
                  <UsersIcon className="w-5 h-5 text-primary" />
                  Tabella Utenti
                </DrawerTitle>
              </DrawerHeader>
              <AdminUsersTable />
            </DrawerContent>
          </Drawer>
        )}

      </div>
    </PageTransition>
  );
};

export default Profile;
